import React, { useState } from 'react';
import {
  Box,
  TextField,
  Button,
  Typography,
  Paper,
  CircularProgress,
  Alert,
} from '@mui/material';
import { useAuth } from '../../context/AuthContext';

const RegisterForm: React.FC = () => {
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    first_name: '',
    last_name: '',
    organization_event_name: ''
  });
  const [confirmPassword, setConfirmPassword] = useState('');
  const [formError, setFormError] = useState('');
  const [success, setSuccess] = useState(false);
  
  const {register, loading, error } = useAuth();
  
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };
  
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError('');
    
  // Basic validation
    if (!formData.email || !formData.password || !formData.first_name || !formData.last_name) {
      setFormError('All fields are required');
      return;
    }
    
    if (formData.password.length < 8) {
      setFormError('Password must be at least 8 characters long');
      return;
    }
    
    if (formData.password !== confirmPassword) {
      setFormError('Passwords do not match');
      return;
    }
    
    try {
      await register({
        email: formData.email,
        password: formData.password,
        firstName: formData.first_name,
        lastName: formData.last_name,
        organizationEventName: formData.organization_event_name || undefined
      });
      setSuccess(true);
      window.location.href = '/approval-pending';
      
      // Reset form
      setFormData({
        email: '',
        password: '',
        first_name: '',
        last_name: '',
        organization_event_name: ''
      });
      setConfirmPassword('');
    } catch (err) {
      // Error is handled in context
    }
  };
  
  return (
    <Paper elevation={3} sx={{ p: 4, maxWidth: 400, mx: 'auto', mt: 4 }}>
      <Typography variant="h5" component="h1" gutterBottom align="center">
        Register
      </Typography>
      
      {success && (
        <Alert severity="success" sx={{ mb: 2 }}>
          Registration successful! Your account is pending approval by an administrator.
        </Alert>
      )}
      
      <Box component="form" onSubmit={handleSubmit} noValidate>
        {(formError || error) && (
          <Typography color="error" sx={{ mb: 2 }}>
            {formError || error}
          </Typography>
        )}
        
        <TextField
          margin="normal"
          required
          fullWidth
          id="first_name"
          label="First Name"
          name="first_name"
          autoFocus
          value={formData.first_name}
          onChange={handleChange}
          disabled={loading}
        />
        
        <TextField
          margin="normal"
          required
          fullWidth
          id="last_name"
          label="Last Name"
          name="last_name"
          value={formData.last_name}
          onChange={handleChange}
          disabled={loading}
        />
        
        <TextField
          margin="normal"
          fullWidth
          id="organization_event_name"
          label="Organization/Event Name"
          name="organization_event_name"
          value={formData.organization_event_name}
          onChange={handleChange}
          disabled={loading}
        />
        
        <TextField
          margin="normal"
          required
          fullWidth
          id="email"
          label="Email Address"
          name="email"
          autoComplete="email"
          type="email"
          value={formData.email}
          onChange={handleChange}
          disabled={loading}
        />
        
        <TextField
          margin="normal"
          required
          fullWidth
          name="password"
          label="Password"
          type="password"
          id="password"
          autoComplete="new-password"
          value={formData.password}
          onChange={handleChange}
          disabled={loading}
          helperText="Password must be at least 8 characters long"
        />
        
        <TextField
          margin="normal"
          required
          fullWidth
          name="confirmPassword"
          label="Confirm Password"
          type="password"
          id="confirmPassword"
          value={confirmPassword}
          onChange={(e) => setConfirmPassword(e.target.value)}
          disabled={loading}
        />
        
        <Button
          type="submit"
          fullWidth
          variant="contained"
          sx={{ mt: 3, mb: 2 }}
          disabled={loading}
        >
          {loading ? <CircularProgress size={24} /> : 'Register'}
        </Button>
      </Box>
    </Paper>
  );
};

export default RegisterForm;